/**
 * Analytics › Versions : versions de l'app exigées et proposées, par plateforme
 * (administrateurs). GET/PUT /api/admin/app-versions.
 *
 * Sous `min_version`, l'API répond 426 à l'app, qui affiche un écran de mise à
 * jour bloquant. Sous `latest_version`, l'app propose la mise à jour sans
 * bloquer. Vide : pas de contrainte.
 */

export type AppPlatform = 'android' | 'ios';
export interface VersionPolicy { min_version: string | null; latest_version: string | null; updated_at?: string | null }
export type VersionPolicies = Record<AppPlatform, VersionPolicy>;
/** Comptes vus dans l'app ces 30 derniers jours, par version (null : version inconnue). */
export type VersionAdoption = Record<AppPlatform, { version: string | null; users: number }[]>;

export interface AppVersionsState {
  policies: VersionPolicies;
  adoption: VersionAdoption;
  store_urls: Record<AppPlatform, string>;
}

const base = () => `${process.env.REACT_APP_API_URL}api/admin/app-versions`;

async function call<T>(token: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(base(), {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init.headers || {}) }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
  return data as T;
}

export const getAppVersions = (token: string) => call<AppVersionsState>(token);

export const updateAppVersions = (token: string, policies: VersionPolicies) =>
  call<AppVersionsState>(token, { method: 'PUT', body: JSON.stringify({ policies }) });

/** Même comparaison que l'API (services/appVersion.service.js) : 1.10 > 1.9, 1.4 = 1.4.0. */
export function compareVersions(a: string, b: string): number {
  const parts = (v: string) => v.split(/[^0-9.]/)[0].split('.').map((n) => Number(n) || 0);
  const left = parts(a);
  const right = parts(b);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  return 0;
}

export const VERSION_PATTERN = /^\d{1,4}(\.\d{1,4}){0,3}$/;
